import { SurfaceDefectChip } from "@/shared/components/SurfaceDefectChip";
import { useSurfaceDefects } from "@/shared/selectors/SurfaceDefectSelector";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	Box,
	IconButton,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { type KeyboardEvent, useEffect, useRef } from "react";
import type { DefectDraft } from "../../models/Reading";
import {
	EDITORS,
	focusEditor,
	navigationStep,
	stepEditor,
} from "@/shared/utils/cellFocus";
import { SurfaceDefectPhoto } from "./SurfaceDefectPhoto";

type PanelItem = {
	SurfaceDefectId: number;
	SurfaceDefectName: string;
	Color: string | null;
};

const EMPTY_LINE: DefectDraft = {
	SurfaceDefectId: 0,
	SurfaceDefectName: "",
	Color: null,
	Comments: null,
	Path: null,
};

const GRID_SX = {
	display: "grid",
	gridTemplateColumns: "30px minmax(150px, 240px) 1fr 30px",
	gap: 0.75,
	alignItems: "center",
	rowGap: 0.75,
} as const;

const FIELD_SX = {
	backgroundColor: "white",
	"& .MuiInputBase-input": { fontSize: 12, py: 0.5 },
	"& .MuiSelect-select": {
		fontSize: 12,
		py: 0.5,
		display: "flex",
		alignItems: "center",
	},
} as const;

type Props = {
	selected: DefectDraft[];
	onChange?: (defects: DefectDraft[]) => void;
	onLeave?: () => void;
};

export const SurfaceDefectsPanel = ({ selected, onChange, onLeave }: Props) => {
	const { data } = useSurfaceDefects();
	const gridRef = useRef<HTMLDivElement>(null);
	const pickedLine = useRef<number | null>(null);

	const catalog: PanelItem[] = (data ?? []).map((defect) => ({
		SurfaceDefectId: defect.SurfaceDefectId as number,
		SurfaceDefectName: defect.SurfaceDefectName,
		Color: defect.Color,
	}));

	const setLine = (index: number, line: DefectDraft) =>
		onChange?.(
			index === selected.length
				? [...selected, line]
				: selected.map((item, position) => (position === index ? line : item)),
		);

	const pickDefect = (index: number, surfaceDefectId: number) => {
		const defect =
			catalog.find((item) => item.SurfaceDefectId === surfaceDefectId) ??
			EMPTY_LINE;

		pickedLine.current = index;
		setLine(index, {
			...defect,
			Comments: selected[index]?.Comments ?? null,
			Path: selected[index]?.Path ?? null,
		});
	};

	const remove = (index: number) =>
		onChange?.(selected.filter((_item, position) => position !== index));

	const editorAt = (line: number, col = "defect") =>
		gridRef.current
			?.querySelector(`[data-line="${line}"][data-col="${col}"]`)
			?.querySelector<HTMLElement>(EDITORS) ?? null;

	useEffect(() => {
		const line = pickedLine.current;

		if (line === null) return;

		pickedLine.current = null;
		focusEditor(editorAt(line, "comment"));
	}, [selected]);

	const onGridKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

		const field = (event.target as HTMLElement).closest<HTMLElement>(
			"[data-line]",
		);

		if (!field) return;

		const line =
			Number(field.dataset.line) + (event.key === "ArrowUp" ? -1 : 1);

		if (line < 0) return;

		if (focusEditor(editorAt(line, field.dataset.col) ?? editorAt(line))) {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const onGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.defaultPrevented) return;

		const target = event.target as HTMLElement;

		if (!target.closest("[data-line]")) return;

		const step = navigationStep(event);

		if (step === null) return;

		if (stepEditor(gridRef.current, target, step)) {
			event.preventDefault();
			return;
		}

		if (step === -1 && onLeave) {
			onLeave();
			event.preventDefault();
		}
	};

	if (!onChange)
		return (
			<Box sx={{ px: 1.5, py: 1 }}>
				{selected.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nothing was found on this reading.
					</Typography>
				) : (
					<Box sx={{ ...GRID_SX, rowGap: 0.5 }}>
						<Header />
						{selected.map((defect) => (
							<Box key={defect.SurfaceDefectId} sx={{ display: "contents" }}>
								<SurfaceDefectPhoto
									name={defect.SurfaceDefectName}
									path={defect.Path}
								/>
								<Box>
									<SurfaceDefectChip
										name={defect.SurfaceDefectName}
										color={defect.Color}
									/>
								</Box>
								<Typography
									sx={{ fontSize: 12 }}
									color="text.secondary"
									data-test-id="defect-comment"
								>
									{defect.Comments || "-"}
								</Typography>
								<Box />
							</Box>
						))}
					</Box>
				)}
			</Box>
		);

	const lines = [...selected, EMPTY_LINE];
	const used = new Set(selected.map((defect) => defect.SurfaceDefectId));

	return (
		<Box sx={{ px: 1.5, py: 1 }}>
			<Box
				ref={gridRef}
				sx={GRID_SX}
				onKeyDownCapture={onGridKeyDownCapture}
				onKeyDown={onGridKeyDown}
			>
				<Header />

				{lines.map((line, index) => {
					const isNew = index === selected.length;
					const options = catalog.filter(
						(item) =>
							item.SurfaceDefectId === line.SurfaceDefectId ||
							!used.has(item.SurfaceDefectId),
					);
					const retired =
						line.SurfaceDefectId !== 0 &&
						!catalog.some(
							(item) => item.SurfaceDefectId === line.SurfaceDefectId,
						);
					const values = retired ? [line, ...options] : options;

					return (
						<Box
							key={line.SurfaceDefectId || "new"}
							sx={{ display: "contents" }}
						>
							{isNew ? (
								<Box />
							) : (
								<SurfaceDefectPhoto
									name={line.SurfaceDefectName}
									path={line.Path}
								/>
							)}

							<TextField
								select
								size="small"
								margin="none"
								fullWidth
								data-line={index}
								data-col="defect"
								data-test-id="defect-select"
								value={line.SurfaceDefectId || ""}
								onChange={(event) =>
									pickDefect(index, Number(event.target.value))
								}
								disabled={values.length === 0}
								slotProps={{
									select: {
										displayEmpty: true,
										renderValue: (picked) => {
											const item = values.find(
												(value) => value.SurfaceDefectId === picked,
											);

											return item ? (
												<SurfaceDefectChip
													name={item.SurfaceDefectName}
													color={item.Color}
												/>
											) : (
												<Box component="span" sx={{ color: "text.disabled" }}>
													{values.length === 0
														? "No defects available"
														: "Choose a defect..."}
												</Box>
											);
										},
										MenuProps: {
											disableEnforceFocus: true,
											disableRestoreFocus: true,
										},
										onClose: () => {
											if (pickedLine.current === null)
												focusEditor(editorAt(index));
										},
									},
								}}
								sx={FIELD_SX}
							>
								{values.map((item) => (
									<MenuItem
										key={item.SurfaceDefectId}
										value={item.SurfaceDefectId}
									>
										<SurfaceDefectChip
											name={item.SurfaceDefectName}
											color={item.Color}
										/>
									</MenuItem>
								))}
							</TextField>

							<TextField
								size="small"
								margin="none"
								fullWidth
								autoComplete="off"
								data-line={index}
								data-col="comment"
								data-test-id="defect-comment-input"
								disabled={isNew}
								placeholder={isNew ? "Pick a defect first" : "Comment"}
								value={line.Comments ?? ""}
								onChange={(event) =>
									setLine(index, { ...line, Comments: event.target.value })
								}
								sx={FIELD_SX}
							/>

							{isNew ? (
								<Box />
							) : (
								<IconButton
									size="small"
									color="error"
									title="Remove defect"
									data-test-id="remove-defect-button"
									sx={{ p: 0.25 }}
									onClick={() => remove(index)}
								>
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							)}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

const Header = () => (
	<>
		<Box />
		<Typography variant="caption" color="text.secondary" fontWeight={600}>
			Surface defect
		</Typography>
		<Typography variant="caption" color="text.secondary" fontWeight={600}>
			Comments
		</Typography>
		<Box />
	</>
);
